import React from "react";
import { useState, useEffect } from "react";
import ingressService from "../../../services/ingressService";
import {
  Alert,
  Button,
  Grid,
  Snackbar,
  TextField,
  Typography,
  Box,
  Paper,
  Chip,
  Tooltip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import DeleteIcon from "@mui/icons-material/Delete";
import { Link, useNavigate } from "react-router-dom";
import userService from "../../../services/userService";
import TablaGenerica from "../../../components/Table/tablaGenerica";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarehouseIcon from "@mui/icons-material/Warehouse";

const initialAlert = {
  severity: "success",
  message: "",
};

const initialForm = {
  type: "in",
  status: "pending",
  sub_movement_id: 0,
  receipt_id: "",
  provider_name: "",
  warehouse_id: 0,
  observation: "",
};

export default function CrearIngreso() {
  const navigate = useNavigate();
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [alertObj, setAlertObj] = useState(initialAlert);
  const [open, setOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [amountEntered, setAmountEntered] = useState("");
  const [cantidadIngresadaErrors, setCantidadIngresadaErrors] = useState({});
  const [inputFocus, setInputFocus] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [formData, setFormData] = useState(initialForm);
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const columns = [
    {
      title: "SKU",
      field: "sku_id",
      width: 50,
      sorting: false,
      render: (rowData) => (
        <Chip
          label={rowData.sku_id}
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: "0.75rem",
            backgroundColor: "#E8F0FE",
            color: "#3949ab",
          }}
        />
      ),
    },
    {
      title: "Descripción",
      field: "name",
      width: 300,
      sorting: false,
      render: (rowData) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {rowData.name}
        </Typography>
      ),
    },
    {
      title: "Stock actual",
      field: "stock",
      width: 40,
      sorting: false,
      render: (rowData) => (
        <Typography variant="body2" fontWeight={500}>
          {rowData.stock}
        </Typography>
      ),
    },
    {
      title: "Stock a ingresar",
      field: "stock_in",
      width: 40,
      sorting: false,
      render: (rowData) => (
        <TextField
          variant="outlined"
          key={rowData?.sku_id}
          size="small"
          type="text"
          placeholder="0"
          sx={{
            width: 80,
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              fontSize: "0.875rem",
              "& fieldset": { borderColor: "#cbd5e1" },
              "&:hover fieldset": { borderColor: "primary.main" },
              "&.Mui-focused fieldset": { borderWidth: 2 },
            },
            "& input": { textAlign: "center", py: 0.8 },
          }}
          value={rowData.amountEntered === null ? "" : rowData.amountEntered}
          onChange={(event) => handleQuantityChange(event, rowData.sku_id)}
          inputProps={{
            pattern: "[0-9]*",
            title: "Ingrese solo números",
          }}
          autoFocus={inputFocus === rowData.sku}
        />
      ),
    },
    {
      title: "Acciones",
      field: "acciones",
      width: 80,
      sorting: false,
      render: (rowData) => (
        <Tooltip title="Eliminar producto" arrow>
          <IconButton
            size="small"
            onClick={() => handleRemoveProduct(rowData.sku_id)}
            sx={{
              color: "#94a3b8",
              "&:hover": { color: "#ef4444", backgroundColor: "#fef2f2" },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  useEffect(() => {
    fetchDepositos();
  }, []);

  useEffect(() => {
    if (inputValue.length < 3) {
      setFilteredProducts([]);
      setIsSearching(false);
      return;
    }

    const timer = setTimeout(() => {
      filterProductsByText(inputValue);
    }, 650);

    return () => clearTimeout(timer);
  }, [inputValue]);

  // Almacén único: lo seleccionamos automáticamente.
  useEffect(() => {
    if (deposits.length === 1) {
      setFormData((prev) => ({ ...prev, warehouse_id: deposits[0].id }));
    }
  }, [deposits]);

  const fetchDepositos = async () => {
    const result = await userService.getDeposits();
    setDeposits(result.data);
  };

  const filterProductsByText = async (text) => {
    setIsSearching(true);
    try {
      const lowercasedText = text.toLowerCase();
      const res = await ingressService.getInventory(text, true, false);
      const products = res?.data?.results || [];
      const filtered = products.filter(
        (product) =>
          product.name.toLowerCase().includes(lowercasedText) ||
          product.sku_id.toLowerCase().includes(lowercasedText)
      );
      setFilteredProducts(filtered);
    } catch (error) {
      console.error("Error al filtrar productos:", error);
      setFilteredProducts([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleProductSelect = (event, value) => {
    if (value) {
      const existentProduct = selectedProducts.find(
        (selectedProduct) => selectedProduct.sku_id === value.sku_id
      );

      if (!existentProduct) {
        setSelectedProducts((prevProductos) => [
          { ...value, amountEntered: null },
          ...prevProductos,
        ]);
      }
      setInputValue("");
    }
  };

  const handleQuantityChange = (event, sku) => {
    const value = event.target.value.trim();
    const errors = { ...cantidadIngresadaErrors };

    if (value === "" || (!isNaN(value) && parseInt(value, 10) >= 0)) {
      errors[sku] = "";
      setSelectedProducts((prevProductos) =>
        prevProductos.map((producto) =>
          producto.sku_id === sku
            ? {
                ...producto,
                amountEntered: value === "" ? null : parseInt(value, 10),
              }
            : producto
        )
      );
    } else {
      errors[sku] = "Campo requerido";
    }

    setCantidadIngresadaErrors(errors);
    setInputFocus(sku);
  };

  const handleRemoveProduct = (sku_id) => {
    setSelectedProducts((prevProductos) =>
      prevProductos.filter((product) => product.sku_id !== sku_id)
    );
  };

  const canConfirm = () => {
    const hasSelectedProducts = selectedProducts.length > 0;
    const hasInvalidQuantity = selectedProducts.some(
      (producto) => !producto.amountEntered || producto.amountEntered <= 0
    );

    return hasSelectedProducts && !hasInvalidQuantity;
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "receipt_id" ? value.toUpperCase() : value,
    }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const calcularTotalUnidadesIngresadas = () => {
    const total = selectedProducts.reduce(
      (accumulator, producto) => accumulator + (producto.amountEntered || 0),
      0
    );
    setAmountEntered(total);
  };

  useEffect(() => {
    calcularTotalUnidadesIngresadas();
  }, [selectedProducts]);

  const handleConfirm = async () => {
    const newFormErrors = {};
    let hasErrors = false;

    // Proveedor y Comprobante son opcionales: no bloquean el alta de stock.
    if (!formData.warehouse_id || formData.warehouse_id === 0) {
      newFormErrors.warehouse_id = "Seleccione un almacén";
      hasErrors = true;
    }

    if (hasErrors) {
      setFormErrors(newFormErrors);
      return;
    }

    const hasCantidadIngresadaErrors = Object.values(
      cantidadIngresadaErrors
    ).some((error) => error !== "");

    if (hasCantidadIngresadaErrors || !canConfirm()) {
      return;
    }

    setLoading(true);

    const requestBody = {
      ...formData,
      movement_details: selectedProducts.map((product) => ({
        product_id: product.id.toString(),
        description: product.observation,
        original_count: product.amountEntered,
        effective_count: 0,
      })),
    };

    try {
      let nuevoIngreso = await ingressService.postCreateIngress(requestBody);
      if (nuevoIngreso.status) {
        setAlertObj({
          severity: "success",
          message: "Ingreso creado exitosamente",
        });
        setOpen(true);
      } else if (nuevoIngreso?.data?.Error) {
        setAlertObj({
          severity: "error",
          message: "Ocurrió un error en el ingreso",
        });
        setOpen(true);
      }
      setTimeout(() => {
        navigate("/ingress-list");
      }, 2000);
    } catch {
      setAlertObj({
        severity: "error",
        message: "Ocurrió un error en el ingreso",
      });
      setOpen(true);
      setLoading(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
    },
  };

  const hasProducts = selectedProducts.length > 0;

  return (
    <Grid container padding={5}>
      <Box sx={{ width: "100%", mb: 3 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: "#1a1a1a",
            pl: 1.5,
            borderLeft: (theme) => `4px solid ${theme.palette.secondary.main}`,
            lineHeight: 1.5,
          }}
        >
          Nuevo ingreso
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          width: "100%",
          p: 2,
          mb: 2,
          borderRadius: "12px",
          border: "1px solid #e0e0e0",
          backgroundColor: "#fafafa",
        }}
      >
        <Autocomplete
          options={filteredProducts}
          getOptionLabel={(option) =>
            `SKU: ${option.sku_id}, Nombre: ${option.name}`
          }
          inputValue={inputValue}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue || "");
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Buscar productos (escribir al menos 3 letras)"
              variant="outlined"
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                },
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <SearchIcon sx={{ color: "#94a3b8", mr: 0.5 }} />
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
          onChange={handleProductSelect}
          noOptionsText={isSearching ? "Buscando..." : "No hay resultados"}
          value={null}
        />
      </Paper>

      <Paper
        elevation={0}
        sx={{
          width: "100%",
          borderRadius: "12px",
          border: "1px solid #e0e0e0",
          overflow: "hidden",
        }}
      >
        <TablaGenerica
          data={selectedProducts}
          columns={columns}
          enablePagination={true}
          emptyMessage="No hay productos agregados"
          tableStyle={{ width: "100%" }}
        />
      </Paper>

      {hasProducts && (
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            mt: 2,
            p: 2.5,
            borderRadius: "12px",
            border: "1px solid #e0e0e0",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.95rem",
              color: "#0f172a",
              mb: 2,
            }}
          >
            Datos del ingreso
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                {deposits.length === 1 ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      border: "1px solid #e2e8f0",
                      borderRadius: "10px",
                      px: 2,
                      py: 1.1,
                      backgroundColor: "#fff",
                    }}
                  >
                    <WarehouseIcon
                      sx={{ fontSize: 18, color: "primary.main" }}
                    />
                    <Typography variant="body2" fontWeight={600}>
                      {deposits[0].name.toUpperCase()}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <InputLabel id="select-label">
                      {formErrors.warehouse_id
                        ? formErrors.warehouse_id
                        : "Almacén *"}
                    </InputLabel>
                    <Select
                      labelId="select-label"
                      id="select"
                      value={formData.warehouse_id}
                      label="Almacén"
                      name="warehouse_id"
                      onChange={handleFormChange}
                      error={!!formErrors.warehouse_id}
                      sx={{ borderRadius: "10px" }}
                    >
                      <MenuItem value="0">Seleccione un almacén</MenuItem>
                      {deposits.map((element) => (
                        <MenuItem key={element.id} value={element.id}>
                          {element.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Proveedor (opcional)"
                name="provider_name"
                value={formData.provider_name}
                onChange={handleFormChange}
                inputProps={{ maxLength: 25 }}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Comprobante asociado (opcional)"
                name="receipt_id"
                value={formData.receipt_id}
                onChange={handleFormChange}
                inputProps={{ maxLength: 25 }}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Observaciones (opcional)"
                name="observation"
                value={formData.observation}
                onChange={handleFormChange}
                inputProps={{ maxLength: 100 }}
                sx={inputSx}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", gap: 2, mt: 2.5 }}>
            <Chip
              label={`${amountEntered} unidades`}
              icon={
                <Box
                  component="span"
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: "#2563EB",
                    ml: 1,
                  }}
                />
              }
              variant="outlined"
              sx={{
                fontWeight: 600,
                fontSize: "0.8rem",
                borderColor: "#bbdefb",
                backgroundColor: "#E8F0FE",
                color: "#2563EB",
              }}
            />
            <Chip
              label={`${selectedProducts.length} SKUs`}
              variant="outlined"
              icon={
                <Box
                  component="span"
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: "#2e7d32",
                    ml: 1,
                  }}
                />
              }
              sx={{
                fontWeight: 600,
                fontSize: "0.8rem",
                borderColor: "#c8e6c9",
                backgroundColor: "#e8f5e9",
                color: "#2e7d32",
              }}
            />
          </Box>
        </Paper>
      )}

      <Box
        sx={{
          width: "100%",
          mt: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Link to="/ingress-list" style={{ textDecoration: "none" }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              borderColor: "#cbd5e1",
              color: "#475569",
              "&:hover": {
                borderColor: "#94a3b8",
                backgroundColor: "#f8fafc",
              },
            }}
          >
            Cancelar
          </Button>
        </Link>

        {hasProducts && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirm}
            disabled={loading || !canConfirm()}
            startIcon={!loading && <CheckCircleOutlineIcon />}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
            }}
          >
            {loading ? "Creando..." : "Confirmar ingreso"}
          </Button>
        )}
      </Box>

      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={alertObj?.severity}
          sx={{ width: "100%" }}
        >
          {alertObj.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
}
